// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.OperatorGroup.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * OperatorGroup Component
 */
import { observer } from 'mobx-react';
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import classnames from 'classnames';
import DropDown from 'share/components/controls/Izenda.DropDown';
import OperatorValue from './Izenda.OperatorValue';
import CONSTANTS from 'logic/data/Izenda.FilterDetailsConfigConstants';
import { get, filter, includes, toLower, isNil, isEmpty } from 'lodash/fp';
import PropTypes from 'prop-types';
import typeof FilterDetailsConfig from 'logic/models/Izenda.FilterDetailsConfig';

@observer
export default class OperatorGroup extends Component {
  static propTypes = {
    configField: PropTypes.object.isRequired,
    updateSourceValue: PropTypes.func.isRequired
  };

  componentWillReceiveProps(nextProps: any) {
    function getPosition(props: any) {
      return get('configField.fieldDetails.position', props);
    }
    if (getPosition(this.props) !== getPosition(nextProps)) {
      this.updateSourceFilter();
    }
  }

  /**
   * Update operator for SP
   */
  updateOperatorForStoreProcedure() {
    const {
      configField,
      configField: {
        operators,
        fieldDetails: { isParameter }
      }
    } = this.props;
    if (isParameter) {
      const allowParameterOperators = filter({ allowParameter: true }, operators);
      allowParameterOperators.length > 0 && configField.setOperatorId(allowParameterOperators[0].id);
    }
  }

  /**
   * Get list operators
   * @param {FilterDetailsConfig} configField
   * @param {boolean} notInReportDesignerMode
   * @param {boolean} isBlankDataType
   */
  getOperators(configField: FilterDetailsConfig, notInReportDesignerMode: boolean, isBlankDataType: boolean) {
    const { operators } = configField;
    const valueList = isBlankDataType ? CONSTANTS.BLANK_VALUE_LIST : CONSTANTS.OPERATOR_VALUE_LIST;

    return notInReportDesignerMode || isBlankDataType
      ? filter(operator => includes(operator.name, valueList), operators)
      : operators;
  }

  render() {
    const {
      configField,
      notInReportDesignerMode,
      configField: {
        operatorSourceValue,
        availableOperatorGroup,
        filterInfo,
        filterInfo: { groupId, operatorId },
        fieldDetails: { isParameter }
      },
      className,
      hideValue,
      store,
      isLazyLoading,
      updateSourceValue
    } = this.props;
    const fieldDataType = get('fieldDetails.dataType', configField);
    const isBlankDataType = CONSTANTS.BLANK_NULL_OPERATOR.indexOf(toLower(fieldDataType)) > -1;
    let operators = this.getOperators(configField, notInReportDesignerMode, isBlankDataType);

    // Only show operator for SP params
    if (isParameter) {
      operators = filter(operator => {
        const paramOperator = get(fieldDataType, CONSTANTS.PARAM_OPERATOR_BY_DATA_TYPE);
        return operator.allowParameter && (!paramOperator || paramOperator.indexOf(operator.key) > -1);
      }, operators);
    }

    const groupClassName = classnames(className, {
      'operator-group': !notInReportDesignerMode
    });

    return (
      <section className={groupClassName}>
        {/* operator group */}
        {!notInReportDesignerMode && (
          <DropDown
            options={availableOperatorGroup}
            onChange={this.onChangeOperatorGroup}
            value={groupId}
            valueField='id'
            labelField='name'
          />
        )}

        {/* operator */}
        {!isEmpty(operators) && (
          <DropDown
            className='operator'
            options={operators}
            onChange={this.onChangeOperator}
            value={operatorId}
            valueField='id'
            labelField='name'
          />
        )}

        {/* filter value*/}
        {!(hideValue === true || isBlankDataType) && (
          <OperatorValue
            filterSource={operatorSourceValue}
            filterInfo={filterInfo}
            updateSourceValue={updateSourceValue}
            operatorGroup={configField}
            onChange={this.onChangeOperatorValue}
            store={store}
            isLazyLoading={isLazyLoading}
            configField={configField}
          />
        )}
      </section>
    );
  }

  /**
   * On change operator group
   */
  onChangeOperatorGroup = ({ value }) => {
    this.props.configField.setOperatorGroupId(value);
    this.updateOperatorForStoreProcedure();
    this.updateSourceFilter();
    this.updateDisableCascading();
  };

  /**
   * On change operator
   */
  onChangeOperator = ({ value }) => {
    this.props.configField.setOperatorId(value);
    this.updateSourceFilter();
    this.updateDisableCascading();
  };

  /**
   * On change operator value
   */
  onChangeOperatorValue = (filterValue: string) => {
    const {
      configField,
      configField: { fieldDetails },
      store
    } = this.props;
    configField.setFilterValue(filterValue);
    store.updateReportDesignerCascadingFilter(fieldDetails);
  };

  /**
   * Update source filter
   */
  updateSourceFilter() {
    const {
      configField,
      configField: { filterInfo, fieldDetails, isUpdateSource },
      notInReportDesignerMode,
      hideValue
    } = this.props;

    const operatorId = filterInfo.operatorId;
    if (isNil(fieldDetails.operatorId) && operatorId) {
      configField.setOperatorId(operatorId);
    }

    if (notInReportDesignerMode) {
      this.props.updateSourceValue(configField);
    } else if (hideValue !== true) {
      configField.resetOperatorSourceValue();
      isUpdateSource && this.props.updateSourceValue(configField);
    }
  }

  /**
   * Update disable cascading when change operator
   */
  updateDisableCascading() {
    const { reportFilters } = this.props;
    reportFilters.disableCascading();
  }

  componentDidUpdate() {
    const operatorGroupElm = ReactDOM.findDOMNode(this);
    let operatorElm = operatorGroupElm.getElementsByClassName('operator');

    operatorElm = operatorElm.length ? operatorElm[0] : null;

    if (operatorGroupElm.getElementsByClassName && operatorGroupElm.getElementsByClassName('btn-popup').length) {
      operatorElm && operatorElm.setAttribute('data-inlineOpValue', true);
    } else {
      operatorElm && operatorElm.setAttribute('data-inlineOpValue', false);
    }
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/Filter/Izenda.OperatorGroup.js